/**
 * OnSiteContactSection - "Customer Contact" form section molecule.
 *
 * Renders the framed section card that wraps the existing
 * ContactFieldset (3-mode picker: Select User / Existing Contact /
 * New Contact). Used by the JobFormTemplate for both work order and
 * inspection forms so the create experience is identical between the
 * two — historically inspections only had a single free-text field
 * for contact_name; the new structured contact data on the inspection
 * record (contact_phone, contact_email, contact_id) lets us reuse the
 * same fieldset here.
 */
import React from 'react';
import { SectionCard } from '../SectionCard';
import { ContactFieldset } from '../ContactFieldset';
import type { ContactMode } from '../ContactFieldset';
import type { QuickAddOptions } from '../../../utils/quickAddUser';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface OnSiteContactSectionProps {
	/** Selected company ID — required for "Existing Contact" mode. */
	companyId: string;
	/** Which contact selection mode is active. */
	contactMode: ContactMode;
	onContactModeChange: (mode: ContactMode) => void;
	/** Resolved contact name (from a picked user/contact or manual entry). */
	contactName: string;
	onContactNameChange: (name: string) => void;
	/** Resolved contact phone (auto-filled when a user/contact is picked). */
	contactPhone: string;
	onContactPhoneChange: (phone: string) => void;
	/** Resolved contact email (auto-filled when a user/contact is picked). */
	contactEmail: string;
	onContactEmailChange: (email: string) => void;
	/** "New Contact" mode fields. */
	newContactFirstName: string;
	onNewContactFirstNameChange: (name: string) => void;
	newContactLastName: string;
	onNewContactLastNameChange: (name: string) => void;
	newContactTitle: string;
	onNewContactTitleChange: (title: string) => void;
	/** "Select User" mode selection. */
	contactUserId: string;
	onContactUserIdChange: (id: string) => void;
	/** "Existing Contact" mode selection. */
	selectedContactId: string;
	onSelectedContactIdChange: (id: string) => void;
	/** Persist the typed-in New Contact immediately; see ContactFieldset. */
	onSaveNewContact?: () => Promise<string | null>;
	/** Offer "+ Quick Add User" on the contact pickers; see ContactFieldset. */
	quickAdd?: QuickAddOptions;
	/** Standard styling tokens used by inputs and labels inside the fieldset. */
	labelClass: string;
	inputClass: string;
	normalBorder: string;
	/** Optional extra classes for the outer wrapper. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const OnSiteContactSection: React.FC<OnSiteContactSectionProps> = (props) => {
	return (
		<SectionCard title="Customer Contact" className={`on-site-contact-section ${props.className ?? ''}`} dataRef="molecule-on-site-contact-section">
			<ContactFieldset
				companyId={props.companyId}
				contactMode={props.contactMode}
				onContactModeChange={props.onContactModeChange}
				contactName={props.contactName}
				onContactNameChange={props.onContactNameChange}
				contactPhone={props.contactPhone}
				onContactPhoneChange={props.onContactPhoneChange}
				contactEmail={props.contactEmail}
				onContactEmailChange={props.onContactEmailChange}
				newContactFirstName={props.newContactFirstName}
				onNewContactFirstNameChange={props.onNewContactFirstNameChange}
				newContactLastName={props.newContactLastName}
				onNewContactLastNameChange={props.onNewContactLastNameChange}
				newContactTitle={props.newContactTitle}
				onNewContactTitleChange={props.onNewContactTitleChange}
				contactUserId={props.contactUserId}
				onContactUserIdChange={props.onContactUserIdChange}
				selectedContactId={props.selectedContactId}
				onSelectedContactIdChange={props.onSelectedContactIdChange}
				onSaveNewContact={props.onSaveNewContact}
				quickAdd={props.quickAdd}
				labelClass={props.labelClass}
				inputClass={props.inputClass}
				normalBorder={props.normalBorder}
			/>
		</SectionCard>
	);
};

export default OnSiteContactSection;
